'use client'
import { api } from '@/api/api'
import type { AllarmeSconosciutoDTO } from '@/models/dto/allarme/allarme-sconosciuto-dto'
import { mapToUnknownAlarm } from '@/models/mappers/alarm/unknows-alarm-mapper'
import type { UnknownAlarm } from '@/models/types/alarm/unknown-alarm'
import { AlarmActionType, type AlarmAction } from '@/types/signal-r-actions'
import { useAtom } from 'jotai'
import { useSession } from 'next-auth/react'
import { createContext, startTransition, useCallback, useContext, useEffect, type JSX, type ReactNode } from 'react'
import { realtimeAlarmsAtom } from './atoms-provider'
import { useSignalR } from './signal-r-provider'

type AlarmsContextProps = {
    alarms: UnknownAlarm[]
    add: (alarm: UnknownAlarm) => void
    remove: (id: number) => void
}

const AlarmsProviderContext = createContext<AlarmsContextProps | undefined>(undefined)

type AlarmsProviderProps = {
    children?: ReactNode
}

function AlarmsProvider(props: AlarmsProviderProps): JSX.Element {
    const [realtimeAlarms, setRealtimeAlarms] = useAtom(realtimeAlarmsAtom)
    const session = useSession()
    const enabled = session.status === 'authenticated'
    const { subscribe, unsubscribe } = useSignalR()

    const add = useCallback(
        (data: UnknownAlarm[] | UnknownAlarm): void => {
            const alarms: UnknownAlarm[] = Array.isArray(data) ? data : [data]

            setRealtimeAlarms((prevAlarms) => {
                const filteredAlarms: UnknownAlarm[] = []
                for (const realtimeAlarm of prevAlarms) {
                    let found = false
                    for (const receivedAlarm of alarms) {
                        if (realtimeAlarm.id === receivedAlarm.id) {
                            found = true
                            break
                        }
                    }
                    if (!found) {
                        filteredAlarms.push(realtimeAlarm)
                    }
                }

                filteredAlarms.push(...alarms)
                filteredAlarms.sort((a, b) => a.timestamplast - b.timestamplast)
                return filteredAlarms
            })
        },
        [setRealtimeAlarms],
    )

    const remove = useCallback(
        (id: number): void => {
            setRealtimeAlarms((prev) => prev.filter((alarm) => alarm.id !== id))
        },
        [setRealtimeAlarms],
    )

    const handleAlarms = useCallback(
        (serializedData: unknown): void => {
            const alarmsDTO = JSON.parse(serializedData as string) as AllarmeSconosciutoDTO[]
            const receivedAlarms = alarmsDTO.map((alarmDTO) => mapToUnknownAlarm(alarmDTO))

            add(receivedAlarms)
        },
        [add],
    )

    const handleAlarmAction = useCallback(
        (serializedData: unknown): void => {
            const parsedData = serializedData as string[]

            const [_topic, serializedAlarmAction] = parsedData
            const alarmAction = JSON.parse(serializedAlarmAction) as AlarmAction

            switch (alarmAction.actionType) {
                case AlarmActionType.Ack: {
                    remove(alarmAction.alarmId)
                    break
                }
                case AlarmActionType.Archive: {
                    //TODO: implement
                    break
                }
                case AlarmActionType.NotifyToPlant: {
                    //TODO: implement
                    break
                }
            }
        },
        [remove],
    )

    useEffect(() => {
        if (!enabled) return
        startTransition(async () => {
            try {
                const { alarms } = await api().alarm().getManyActive('99', undefined, { cache: 'no-cache' })
                setRealtimeAlarms(alarms ?? [])
            } catch {
                setRealtimeAlarms([])
            }
        })
        subscribe('Alarms', handleAlarms)
        subscribe('AlarmAction', handleAlarmAction)

        return () => {
            unsubscribe('Alarms', handleAlarms)
            unsubscribe('AlarmAction', handleAlarmAction)
        }
    }, [enabled, handleAlarmAction, handleAlarms, setRealtimeAlarms, subscribe, unsubscribe])

    return (
        <AlarmsProviderContext.Provider value={{ add, alarms: realtimeAlarms, remove }}>
            {props.children}
        </AlarmsProviderContext.Provider>
    )
}

const useAlarms = (): AlarmsContextProps => {
    const context = useContext(AlarmsProviderContext)
    if (!context) {
        throw new Error("useAlarms deve essere usato all'interno di un AlarmsProvider")
    }
    return context
}

export { AlarmsProvider, useAlarms }
